import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
    FileText, Puzzle, Sparkles, Check, X, Edit3, Loader2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";

/**
 * Phase 8A — Admin Autonomy Control Panel.
 *
 * Aggregates AI-generated pending items across:
 *   - blog_drafts        (blog_posts.status == "draft")
 *   - module_suggestions (custom_modules.status == "draft")
 *   - dynamic_offers     (dynamic_offers.status == "active")
 *
 * Bulk actions per row: Approve, Reject, Edit. All admin actions are
 * captured in admin_audit_log via the AdminAuditMiddleware.
 */

const TABS = [
    { key: "all", label: "All", icon: Sparkles },
    { key: "blog_drafts", label: "Blog drafts", icon: FileText },
    { key: "module_suggestions", label: "Modules", icon: Puzzle },
    { key: "dynamic_offers", label: "Offers", icon: Sparkles },
];

function Badge({ children, color = "primary" }) {
    const colors = {
        primary: "text-[var(--color-primary)] bg-[var(--color-primary)]/10 border-[var(--color-primary)]/30",
        secondary: "text-[var(--color-secondary)] bg-[var(--color-secondary)]/10 border-[var(--color-secondary)]/30",
        success: "text-[var(--color-success)] bg-[var(--color-success)]/10 border-[var(--color-success)]/30",
        warning: "text-neutral-400 bg-neutral-400/10 border-neutral-400/30",
    };
    return (
        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${colors[color] || colors.primary}`}>
            {children}
        </span>
    );
}

function ItemCard({ entity, type, onApprove, onReject, onEdit, busy }) {
    const title =
        type === "blog_drafts" ? entity.title
        : type === "module_suggestions" ? entity.name
        : type === "dynamic_offers" ? `${entity.offer_type} · ${entity.discount_percentage}% off`
        : "";
    const excerpt =
        type === "blog_drafts" ? (entity.excerpt || "")
        : type === "module_suggestions" ? (entity.description || "")
        : type === "dynamic_offers" ? `Target: ${entity.target_plan_id || "any"} · Expires ${(entity.expires_at || "").slice(0, 10)}`
        : "";
    const auto =
        type === "blog_drafts" ? entity.auto_generated
        : type === "module_suggestions" ? entity.ai_suggested
        : type === "dynamic_offers" ? !!(entity.meta && entity.meta.source)
        : false;

    return (
        <motion.div
            layout
            data-testid={`autonomy-item-${type}-${entity.id}`}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5"
        >
            <div className="flex items-start gap-3 mb-2">
                <h3 className="text-sm md:text-base font-semibold text-text-main flex-1 min-w-0 truncate">
                    {title || "(untitled)"}
                </h3>
                {auto && <Badge color="secondary">AI-generated</Badge>}
                <Badge color="warning">{type.replace("_", " ")}</Badge>
            </div>
            <p className="text-xs text-text-secondary mb-3 line-clamp-3">{excerpt || "—"}</p>
            <div className="flex flex-wrap items-center gap-2 text-[10px] text-text-secondary">
                <span>id: <code className="font-mono">{entity.id.slice(0, 8)}</code></span>
                {entity.created_at && <span>· created {entity.created_at.slice(0, 10)}</span>}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                    onClick={() => onApprove(type, entity)}
                    disabled={busy}
                    data-testid={`autonomy-approve-${entity.id}`}
                    className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-1.5 font-medium hover:opacity-90 flex items-center gap-1 disabled:opacity-50"
                >
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                    Approve
                </button>
                <button
                    onClick={() => onReject(type, entity)}
                    disabled={busy}
                    data-testid={`autonomy-reject-${entity.id}`}
                    className="text-xs rounded-full border border-[var(--color-border)] text-text-main px-4 py-1.5 font-medium hover:border-[var(--color-alert)]/40 hover:text-[var(--color-alert)] transition-colors flex items-center gap-1"
                >
                    <X className="h-3 w-3" />
                    Reject
                </button>
                <button
                    onClick={() => onEdit(type, entity)}
                    disabled={busy}
                    data-testid={`autonomy-edit-${entity.id}`}
                    className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary px-4 py-1.5 hover:text-text-main hover:border-[var(--color-primary)]/40 transition-colors flex items-center gap-1"
                >
                    <Edit3 className="h-3 w-3" />
                    Edit
                </button>
            </div>
        </motion.div>
    );
}

function EditModal({ open, onClose, entity, type, onSave }) {
    const [form, setForm] = useState({});
    useEffect(() => { if (open) setForm(entity || {}); }, [open, entity]);
    if (!open) return null;

    const fields = (
        type === "blog_drafts" ? [
            { key: "title", label: "Title", type: "text" },
            { key: "excerpt", label: "Excerpt", type: "textarea", rows: 3 },
            { key: "content_markdown", label: "Content (Markdown)", type: "textarea", rows: 12 },
        ] : type === "module_suggestions" ? [
            { key: "name", label: "Name", type: "text" },
            { key: "description", label: "Description", type: "textarea", rows: 3 },
            { key: "cost_credits", label: "Cost (credits)", type: "number" },
        ] : type === "dynamic_offers" ? [
            { key: "discount_percentage", label: "Discount %", type: "number" },
            { key: "expires_at", label: "Expires at (ISO)", type: "text" },
        ] : []
    );

    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm p-4 md:p-8 flex items-center justify-center"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                data-testid="autonomy-edit-modal"
                className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[var(--color-card)] border border-[var(--color-border)] rounded-2xl p-6"
            >
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-text-main">Edit {type.replace("_", " ")}</h2>
                    <button onClick={onClose} className="text-text-secondary hover:text-text-main"><X className="h-4 w-4" /></button>
                </div>
                <div className="space-y-4">
                    {fields.map((f) => (
                        <label key={f.key} className="block">
                            <span className="text-[11px] uppercase tracking-widest text-text-secondary block mb-1">
                                {f.label}
                            </span>
                            {f.type === "textarea" ? (
                                <textarea
                                    rows={f.rows || 4}
                                    value={form[f.key] ?? ""}
                                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                                    className="w-full rounded-lg bg-[var(--color-bg)] border border-[var(--color-border)] px-3 py-2 text-sm text-text-main focus:border-[var(--color-primary)] outline-none"
                                />
                            ) : (
                                <input
                                    type={f.type}
                                    value={form[f.key] ?? ""}
                                    onChange={(e) => setForm((s) => ({
                                        ...s,
                                        [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value,
                                    }))}
                                    className="w-full rounded-lg bg-[var(--color-bg)] border border-[var(--color-border)] px-3 py-2 text-sm text-text-main focus:border-[var(--color-primary)] outline-none"
                                />
                            )}
                        </label>
                    ))}
                </div>
                <div className="mt-6 flex items-center gap-2 justify-end">
                    <button
                        onClick={onClose}
                        className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary px-4 py-2 hover:text-text-main"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="autonomy-edit-save"
                        onClick={() => onSave(form)}
                        className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-5 py-2 font-medium hover:opacity-90"
                    >
                        Save changes
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function AdminAutonomy() {
    const { user } = useAuth();
    const [queue, setQueue] = useState({
        blog_drafts: [], module_suggestions: [], dynamic_offers: [], total_pending: 0,
    });
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("all");
    const [onlyAuto, setOnlyAuto] = useState(false);
    const [busy, setBusy] = useState(null); // entity.id currently being mutated
    const [edit, setEdit] = useState({ open: false, entity: null, type: null });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const q = onlyAuto ? "?only_auto=true" : "";
            const res = await apiFetch(`/admin/autonomy/queue${q}`);
            setQueue(res);
        } catch (e) {
            toast.error(e.message || "Failed to load queue");
        } finally {
            setLoading(false);
        }
    }, [onlyAuto]);

    useEffect(() => { load(); }, [load]);

    const combined = useMemo(() => {
        if (tab === "blog_drafts")
            return queue.blog_drafts.map((x) => ({ ...x, __type: "blog_drafts" }));
        if (tab === "module_suggestions")
            return queue.module_suggestions.map((x) => ({ ...x, __type: "module_suggestions" }));
        if (tab === "dynamic_offers")
            return queue.dynamic_offers.map((x) => ({ ...x, __type: "dynamic_offers" }));
        return [
            ...queue.blog_drafts.map((x) => ({ ...x, __type: "blog_drafts" })),
            ...queue.module_suggestions.map((x) => ({ ...x, __type: "module_suggestions" })),
            ...queue.dynamic_offers.map((x) => ({ ...x, __type: "dynamic_offers" })),
        ];
    }, [queue, tab]);

    const doApprove = async (type, entity) => {
        setBusy(entity.id);
        try {
            await apiFetch(`/admin/autonomy/approve/${type}/${entity.id}`, {
                method: "POST", body: JSON.stringify({}),
            });
            toast.success("Approved.");
            await load();
        } catch (e) { toast.error(e.message || "Approve failed"); }
        finally { setBusy(null); }
    };

    const doReject = async (type, entity) => {
        const reason = prompt("Rejection reason (optional):") || "";
        setBusy(entity.id);
        try {
            await apiFetch(`/admin/autonomy/reject/${type}/${entity.id}`, {
                method: "POST", body: JSON.stringify({ reason }),
            });
            toast.success("Rejected.");
            await load();
        } catch (e) { toast.error(e.message || "Reject failed"); }
        finally { setBusy(null); }
    };

    const doEdit = (type, entity) => setEdit({ open: true, entity, type });

    const doSaveEdit = async (form) => {
        const { type, entity } = edit;
        setBusy(entity.id);
        try {
            // Compute delta so we don't overwrite non-editable fields
            const delta = {};
            const editableKeys = {
                blog_drafts: ["title", "excerpt", "content_markdown", "tags", "meta_description"],
                module_suggestions: ["name", "description", "category", "cost_credits", "schema", "prompt"],
                dynamic_offers: ["discount_percentage", "expires_at", "target_plan_id", "target_interval"],
            }[type] || [];
            for (const k of editableKeys) {
                if (form[k] !== undefined && form[k] !== entity[k]) delta[k] = form[k];
            }
            await apiFetch(`/admin/autonomy/${type}/${entity.id}`, {
                method: "PATCH", body: JSON.stringify({ edits: delta }),
            });
            toast.success("Saved.");
            setEdit({ open: false, entity: null, type: null });
            await load();
        } catch (e) { toast.error(e.message || "Save failed"); }
        finally { setBusy(null); }
    };

    if (!isAdminRole(user?.role)) {
        return (
            <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center p-8">
                <div className="text-center">
                    <h1 className="text-2xl font-semibold mb-2">Admin only</h1>
                    <p className="text-text-secondary">You need an admin role to access this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main className="flex-1 p-6 md:p-8 max-w-6xl mx-auto w-full" data-testid="admin-autonomy">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">
                                Autonomy control
                            </div>
                            <h1 className="text-3xl md:text-4xl font-semibold text-text-main">
                                Review queue · {queue.total_pending}
                            </h1>
                            <p className="text-sm text-text-secondary mt-1">
                                Approve, reject or edit every AI-generated item before it goes live.
                            </p>
                        </div>
                        <button
                            onClick={load}
                            data-testid="autonomy-refresh"
                            className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 flex items-center gap-2"
                        >
                            <RefreshCw className="h-3 w-3" /> Refresh
                        </button>
                    </div>

                    <div className="flex items-center gap-2 mb-6 overflow-x-auto no-scrollbar">
                        {TABS.map((t) => {
                            const count =
                                t.key === "all" ? queue.total_pending :
                                t.key === "blog_drafts" ? queue.blog_drafts.length :
                                t.key === "module_suggestions" ? queue.module_suggestions.length :
                                queue.dynamic_offers.length;
                            const Icon = t.icon;
                            return (
                                <button
                                    key={t.key}
                                    data-testid={`autonomy-tab-${t.key}`}
                                    onClick={() => setTab(t.key)}
                                    className={`text-xs uppercase tracking-widest px-4 py-2 rounded-full transition-colors flex items-center gap-1.5 shrink-0 ${
                                        tab === t.key
                                            ? "bg-white hover:bg-[#EDEDED] text-black"
                                            : "border border-[var(--color-border)] text-text-secondary hover:text-text-main"
                                    }`}
                                >
                                    <Icon className="h-3 w-3" />
                                    {t.label} · {count}
                                </button>
                            );
                        })}
                        <label className="ml-auto flex items-center gap-2 text-xs text-text-secondary shrink-0">
                            <input
                                type="checkbox"
                                checked={onlyAuto}
                                onChange={(e) => setOnlyAuto(e.target.checked)}
                                data-testid="autonomy-only-auto"
                            />
                            Show only auto-generated
                        </label>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
                        </div>
                    ) : combined.length === 0 ? (
                        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-12 text-center">
                            <Sparkles className="h-8 w-8 mx-auto text-[var(--color-primary)] mb-3" />
                            <h3 className="text-lg font-semibold mb-1">Queue clear</h3>
                            <p className="text-sm text-text-secondary">Nothing awaits your review right now.</p>
                        </div>
                    ) : (
                        <div className="grid gap-4 md:grid-cols-2">
                            {combined.map((entity) => (
                                <ItemCard
                                    key={`${entity.__type}:${entity.id}`}
                                    entity={entity}
                                    type={entity.__type}
                                    busy={busy === entity.id}
                                    onApprove={doApprove}
                                    onReject={doReject}
                                    onEdit={doEdit}
                                />
                            ))}
                        </div>
                    )}
                </main>
            </div>
            <EditModal
                open={edit.open}
                entity={edit.entity}
                type={edit.type}
                onClose={() => setEdit({ open: false, entity: null, type: null })}
                onSave={doSaveEdit}
            />
        </div>
    );
}
